"use client";

import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef, useCallback } from "react";

import { formatPrice } from "#/lib/format";
import { useOrgHref } from "#/lib/org-context";

// ─── Types ───

type Service = {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  priceInCents: number;
  maxParticipants: number;
};
type Program = { id: string; name: string; services: Service[] };
type Location = { id: string; name: string; address: string | null };
type Policy = { id: string; text: string };
type Availability = { dayOfWeek: number; startTime: string; endTime: string };
type InstructorService = { serviceId: string; priceInCents: number };

type Coach = {
  id: string;
  name: string | null;
  bio: string | null;
  image: string | null;
  slug: string;
  headline?: string | null;
  extendedBio?: string | null;
  pronouns?: string | null;
  specialties?: string[];
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  facebookUrl?: string | null;
  instructorServices: InstructorService[];
  availability: Availability[];
  locations: Location[];
};

type Org = {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  colorPrimary: string | null;
  colorSecondary: string | null;
  fontPrimary: string | null;
  fontSecondary: string | null;
  welcomeMessage: string | null;
  phone: string | null;
  websiteUrl: string | null;
  replyToEmail: string | null;
  description: string | null;
  coverPhotoUrl: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  theme: string;
  locations: Location[];
  programs: Program[];
  bookingPolicies: Policy[];
};

// ─── Helpers ───

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function FadeInSection({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <div
      className={`animate-in fade-in slide-in-from-bottom-3 fill-mode-both ${className}`}
      style={{ animationDelay: `${delay}ms`, animationDuration: "500ms" }}
    >
      {children}
    </div>
  );
}

// ─── Upcoming Availability Types ───

type UpcomingSlot = {
  startTime: string;
  endTime: string;
  priceInCents: number;
  locations: { id: string; name: string; address?: string | null }[];
  serviceId: string;
  serviceName?: string;
  durationMinutes: number;
};

type UpcomingDay = {
  date: string;
  slots: UpcomingSlot[];
  loading: boolean;
};

// ─── Upcoming Availability Hook ───

function useUpcomingAvailability(orgSlug: string, instructorId: string) {
  const [days, setDays] = useState<UpcomingDay[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedSlots = useRef(new Set<string>());

  useEffect(() => {
    const today = new Date();
    const m1 = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const m2 = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    Promise.all([
      fetch(
        `/api/availability/dates?month=${m1}&instructorId=${instructorId}&orgSlug=${encodeURIComponent(orgSlug)}`,
        { cache: "no-store" },
      ).then((r) => r.json()),
      fetch(
        `/api/availability/dates?month=${m2}&instructorId=${instructorId}&orgSlug=${encodeURIComponent(orgSlug)}`,
        { cache: "no-store" },
      ).then((r) => r.json()),
    ])
      .then(([r1, r2]) => {
        const allDates: string[] = [...(r1.dates || []), ...(r2.dates || [])];
        const upcoming = allDates
          .filter((d: string) => d >= todayStr)
          .sort()
          .slice(0, 7);
        setDays(upcoming.map((d: string) => ({ date: d, slots: [], loading: true })));
        setLoading(false);

        upcoming.forEach((date: string) => {
          if (fetchedSlots.current.has(date)) return;
          fetchedSlots.current.add(date);
          fetch(
            `/api/availability?date=${date}&instructorId=${instructorId}&orgSlug=${encodeURIComponent(orgSlug)}`,
            { cache: "no-store" },
          )
            .then((r) => r.json())
            .then((data) => {
              setDays((prev) =>
                prev.map((d) =>
                  d.date === date ? { ...d, slots: data.slots || [], loading: false } : d,
                ),
              );
            })
            .catch(() => {
              setDays((prev) => prev.map((d) => (d.date === date ? { ...d, loading: false } : d)));
            });
        });
      })
      .catch(() => setLoading(false));
  }, [orgSlug, instructorId]);

  const loadSlotsForDate = useCallback(
    (date: string, iid: string) => {
      if (fetchedSlots.current.has(date)) return;
      fetchedSlots.current.add(date);
      fetch(
        `/api/availability?date=${date}&instructorId=${iid}&orgSlug=${encodeURIComponent(orgSlug)}`,
        { cache: "no-store" },
      )
        .then((r) => r.json())
        .then((data) => {
          setDays((prev) =>
            prev.map((d) =>
              d.date === date ? { ...d, slots: data.slots || [], loading: false } : d,
            ),
          );
        })
        .catch(() => {
          setDays((prev) => prev.map((d) => (d.date === date ? { ...d, loading: false } : d)));
        });
    },
    [orgSlug],
  );

  return { days, loading, loadSlotsForDate };
}

// ─── Scrollable Slot Row ───

function SlotScrollRow({
  day,
  accent,
  coachId,
  loadSlots,
}: {
  day: UpcomingDay;
  accent: string;
  coachId: string;
  loadSlots: (date: string, id: string) => void;
}) {
  const orgHref = useOrgHref();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadSlots(day.date, coachId);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [day.date, coachId, loadSlots]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
  }, [day.slots, checkScroll]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  const formatSlotTime = (iso: string) => {
    const d = new Date(iso);
    const h = d.getHours() % 12 || 12;
    const m = d.getMinutes();
    const period = d.getHours() >= 12 ? "PM" : "AM";
    return m === 0 ? `${h} ${period}` : `${h}:${String(m).padStart(2, "0")} ${period}`;
  };

  const formatDayLabel = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00");
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (isSameDay(d, today)) return "Today";
    if (isSameDay(d, tomorrow)) return "Tomorrow";
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const locations =
    day.slots.length > 0
      ? [...new Map(day.slots.flatMap((s) => s.locations).map((l) => [l.id, l])).values()]
      : [];

  const deduped = (() => {
    if (day.slots.length === 0) return [];
    const byTime = new Map<string, { slot: UpcomingSlot; count: number; minPrice: number }>();
    for (const s of day.slots) {
      const timeKey = formatSlotTime(s.startTime);
      const existing = byTime.get(timeKey);
      if (existing) {
        existing.count++;
        if (s.priceInCents < existing.minPrice) existing.minPrice = s.priceInCents;
      } else {
        byTime.set(timeKey, { slot: s, count: 1, minPrice: s.priceInCents });
      }
    }
    return [...byTime.values()];
  })();

  return (
    <div ref={sentinelRef} className="py-4 px-4 sm:px-5">
      <div className="flex items-baseline justify-between mb-1">
        <span
          className="text-md font-bold tracking-[-0.02em]"
          style={{ color: "var(--sf-text-primary)" }}
        >
          {formatDayLabel(day.date)}
        </span>
      </div>

      {locations.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} strokeWidth={2} />
          <span className="text-tiny font-semibold" style={{ color: "var(--sf-text-primary)" }}>
            {locations.map((l) => [l.name, l.address].filter(Boolean).join(" · ")).join(" / ")}
          </span>
        </div>
      )}

      {day.loading ? (
        <div className="flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-14 w-32 rounded-lg animate-pulse"
              style={{ backgroundColor: "var(--sf-bg-elevated)" }}
            />
          ))}
        </div>
      ) : deduped.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--sf-text-tertiary)" }}>
          No slots available
        </p>
      ) : (
        <div className="relative group">
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 h-8 w-8 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ backgroundColor: "var(--sf-bg-card)", border: "1px solid var(--sf-border)" }}
            >
              <ChevronLeftIcon className="h-4 w-4" style={{ color: "var(--sf-text-secondary)" }} />
            </button>
          )}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {deduped.map(({ slot, count, minPrice }, i) => (
              <Link
                key={i}
                href={orgHref(`/book?cid=${coachId}`)}
                className="shrink-0 flex flex-col items-center gap-0.5 px-4 py-3 rounded-xl text-sm transition-all duration-base hover:scale-[1.03] hover:shadow-md min-w-[76px]"
                style={{
                  backgroundColor: "var(--sf-bg-elevated)",
                  border: "1px solid var(--sf-border)",
                }}
              >
                <span
                  className="font-bold text-sm tracking-[-0.02em]"
                  style={{ color: "var(--sf-text-primary)" }}
                >
                  {formatSlotTime(slot.startTime)}
                </span>
                <span className="text-xs font-bold" style={{ color: accent }}>
                  {count > 1 ? `from ${formatPrice(minPrice)}` : formatPrice(minPrice)}
                </span>
                {count > 1 && (
                  <span
                    className="text-2xs font-medium"
                    style={{ color: "var(--sf-text-tertiary)" }}
                  >
                    {count} options
                  </span>
                )}
              </Link>
            ))}
          </div>
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 h-8 w-8 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ backgroundColor: "var(--sf-bg-card)", border: "1px solid var(--sf-border)" }}
            >
              <ChevronRightIcon className="h-4 w-4" style={{ color: "var(--sf-text-secondary)" }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Component ───

export default function CoachStorefront({ org, coach }: { org: Org; coach: Coach }) {
  const orgHref = useOrgHref();
  const accent = org.colorPrimary || "#0066FF";
  const isDark = org.theme === "dark";

  const allServices = org.programs.flatMap((p) => p.services);
  const availDays = [...new Set(coach.availability.map((a) => a.dayOfWeek))].sort();

  const {
    days: upcomingDays,
    loading: upcomingLoading,
    loadSlotsForDate,
  } = useUpcomingAvailability(org.slug, coach.id);

  const coachFirstName = (() => {
    const full = coach.name || "Instructor";
    const parts = full.split(" ").filter(Boolean);
    if (parts.length > 1 && parts[0].toLowerCase() === "coach") return parts[1];
    return parts[0] || full;
  })();

  // Get effective price for a service (coach override or service default)
  const getEffectivePrice = (service: Service) => {
    const override = coach.instructorServices.find((is) => is.serviceId === service.id);
    return override ? override.priceInCents : service.priceInCents;
  };

  return (
    <div
      className="min-h-screen"
      data-storefront-theme="light"
      style={{
        fontFamily: org.fontPrimary || "Inter, system-ui, -apple-system, sans-serif",
        backgroundColor: "var(--sf-bg)",
        color: "var(--sf-text-primary)",
      }}
    >
      {/* ═══ NAVIGATION BAR ═══ */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-2xl border-b"
        style={{
          backgroundColor: "rgba(255,255,255,0.88)",
          borderColor: "var(--sf-border)",
        }}
      >
        <div className="max-w-5xl mx-auto px-5 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Link
              href={orgHref("/")}
              className="flex items-center gap-1.5 text-tiny font-medium transition-colors rounded-lg mr-1"
              style={{ color: "var(--sf-text-secondary)" }}
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{org.name}</span>
            </Link>
            {org.logo && (
              <Image
                src={org.logo}
                alt={org.name}
                width={28}
                height={28}
                className="h-7 w-auto object-contain"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={orgHref("/login")}
              className="text-tiny font-medium transition-colors px-4 py-1.5 rounded-xl"
              style={{ color: "var(--sf-text-secondary)" }}
            >
              Sign In
            </Link>
            <Link
              href={orgHref(`/book?cid=${coach.id}`)}
              className="text-tiny font-semibold text-white px-5 py-2 rounded-xl transition-all hover:opacity-90 active:scale-[0.97]"
              style={{ backgroundColor: accent }}
            >
              Book with {coachFirstName}
            </Link>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="pb-28 lg:pb-0">
        {/* ═══ HERO SECTION ═══ */}
        <FadeInSection>
          <section className="relative overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
            <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-28">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                {/* Text content */}
                <div className="flex-1 text-center lg:text-left">
                  <p
                    className="text-tiny font-semibold uppercase tracking-[0.08em] mb-3"
                    style={{ color: accent }}
                  >
                    {org.name}
                  </p>
                  <h1
                    className="text-[clamp(2rem,6vw,3.25rem)] font-bold leading-[1.06] tracking-[-0.035em]"
                    style={{ color: "var(--sf-text-primary)" }}
                  >
                    Book with <br />
                    <span style={{ color: accent }}>{coach.name}</span>
                    {coach.pronouns && (
                      <span
                        className="text-base font-normal ml-2"
                        style={{ color: "var(--sf-text-tertiary)" }}
                      >
                        ({coach.pronouns})
                      </span>
                    )}
                  </h1>

                  {coach.headline && (
                    <p className="mt-2 text-base sm:text-lg font-medium" style={{ color: accent }}>
                      {coach.headline}
                    </p>
                  )}

                  {(coach.extendedBio || coach.bio) && (
                    <p
                      className="mt-4 text-md sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0"
                      style={{ color: "var(--sf-text-secondary)" }}
                    >
                      {coach.extendedBio || coach.bio}
                    </p>
                  )}

                  {/* Specialties */}
                  {coach.specialties && coach.specialties.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5 lg:justify-start justify-center">
                      {coach.specialties.map((tag) => (
                        <span
                          key={tag}
                          className="text-micro font-medium px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: `${accent}12`, color: accent }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Social links */}
                  {(coach.instagramUrl || coach.facebookUrl || coach.tiktokUrl) && (
                    <div className="mt-4 flex items-center gap-3 lg:justify-start justify-center">
                      {coach.instagramUrl && (
                        <a
                          href={coach.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-70 transition-opacity"
                          style={{ color: "var(--sf-text-tertiary)" }}
                          title="Instagram"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                          >
                            <rect x="2" y="2" width="20" height="20" rx="5" />
                            <circle cx="12" cy="12" r="5" />
                            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                          </svg>
                        </a>
                      )}
                      {coach.facebookUrl && (
                        <a
                          href={coach.facebookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-70 transition-opacity"
                          style={{ color: "var(--sf-text-tertiary)" }}
                          title="Facebook"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                            <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                          </svg>
                        </a>
                      )}
                      {coach.tiktokUrl && (
                        <a
                          href={coach.tiktokUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:opacity-70 transition-opacity"
                          style={{ color: "var(--sf-text-tertiary)" }}
                          title="TikTok"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.81.11v-3.5a6.37 6.37 0 00-.81-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.72 4.57c1.7-1.7 2.65-4 2.65-6.42V9.4a8.16 8.16 0 003.07.6V6.69z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}

                  {/* Quick stats */}
                  <div
                    className="mt-5 flex items-center gap-4 lg:justify-start justify-center flex-wrap text-tiny font-medium"
                    style={{ color: "var(--sf-text-tertiary)" }}
                  >
                    {allServices.length > 0 && (
                      <span>
                        {allServices.length}{" "}
                        {allServices.length === 1 ? "session type" : "session types"}
                      </span>
                    )}
                    {allServices.length > 0 && availDays.length > 0 && (
                      <span className="h-3 w-px" style={{ backgroundColor: "var(--sf-border)" }} />
                    )}
                    {availDays.length > 0 && (
                      <span>{availDays.map((d) => DAY_SHORT[d]).join(", ")}</span>
                    )}
                    {coach.locations.length > 0 && (
                      <>
                        <span
                          className="h-3 w-px"
                          style={{ backgroundColor: "var(--sf-border)" }}
                        />
                        <span className="flex items-center gap-1">
                          <MapPinIcon className="h-3 w-3" />
                          {coach.locations.map((l) => l.name).join(", ")}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="mt-7 flex flex-col sm:flex-row items-center gap-3 lg:justify-start justify-center">
                    <Link
                      href={orgHref(`/book?cid=${coach.id}`)}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-semibold text-md transition-all hover:-translate-y-0.5 active:scale-[0.97]"
                      style={{
                        backgroundColor: accent,
                        boxShadow: `0 4px 16px ${accent}30, 0 1px 3px rgba(0,0,0,0.08)`,
                      }}
                    >
                      Book a Session
                      <ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                {/* Coach photo */}
                <div className="flex-shrink-0">
                  {coach.image ? (
                    <div className="relative">
                      <Image
                        src={coach.image}
                        alt={coach.name || "Instructor"}
                        width={400}
                        height={480}
                        className="relative w-[260px] sm:w-[300px] lg:w-[340px] h-[320px] sm:h-[370px] lg:h-[420px] object-cover rounded-2xl"
                        style={{
                          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
                        }}
                      />
                      <div
                        className="absolute -bottom-3 left-4 right-4 rounded-xl px-4 py-3 backdrop-blur-xl"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.92)",
                          border: "1px solid var(--sf-border)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                        }}
                      >
                        <p
                          className="text-sm font-semibold tracking-[-0.01em]"
                          style={{ color: "var(--sf-text-primary)" }}
                        >
                          {coach.name}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: accent }}>
                          {org.name}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-[260px] h-[320px] rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--sf-bg-elevated)",
                        border: "1px solid var(--sf-border)",
                      }}
                    >
                      <div className="text-center">
                        <div
                          className="h-24 w-24 rounded-full mx-auto flex items-center justify-center mb-3"
                          style={{ backgroundColor: "var(--sf-bg)" }}
                        >
                          <UserIcon
                            className="h-12 w-12"
                            style={{ color: "var(--sf-text-muted)" }}
                          />
                        </div>
                        <p
                          className="text-md font-semibold"
                          style={{ color: "var(--sf-text-primary)" }}
                        >
                          {coach.name}
                        </p>
                        <p className="text-xs mt-1" style={{ color: accent }}>
                          {org.name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* ═══ UPCOMING AVAILABILITY ═══ */}
        <FadeInSection delay={150}>
          <section className="max-w-5xl mx-auto px-5 sm:px-6 mt-20 sm:mt-28">
            <h2
              className="text-xl sm:text-2xl font-bold tracking-[-0.03em] mb-1"
              style={{ color: "var(--sf-text-primary)" }}
            >
              Upcoming Availability
            </h2>
            <p className="text-sm mb-5" style={{ color: "var(--sf-text-secondary)" }}>
              Click any time to book directly with {coachFirstName}
            </p>

            <div
              className="rounded-2xl overflow-hidden divide-y"
              style={{
                backgroundColor: "var(--sf-bg-card)",
                border: "1px solid var(--sf-border)",
              }}
            >
              {upcomingLoading ? (
                <div className="p-6 space-y-4">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-16 rounded-lg animate-pulse"
                      style={{ backgroundColor: "var(--sf-bg-elevated)" }}
                    />
                  ))}
                </div>
              ) : upcomingDays.length === 0 ? (
                <div className="p-8 text-center">
                  <CalendarIcon
                    className="h-10 w-10 mx-auto mb-3"
                    style={{ color: "var(--sf-text-muted)" }}
                  />
                  <p className="text-md font-medium" style={{ color: "var(--sf-text-secondary)" }}>
                    No upcoming availability
                  </p>
                  <p className="text-tiny mt-1" style={{ color: "var(--sf-text-tertiary)" }}>
                    Check back later or contact {org.name} for more options.
                  </p>
                </div>
              ) : (
                upcomingDays.map((day) => (
                  <SlotScrollRow
                    key={day.date}
                    day={day}
                    accent={accent}
                    coachId={coach.id}
                    loadSlots={loadSlotsForDate}
                  />
                ))
              )}
            </div>
          </section>
        </FadeInSection>

        {/* ═══ SERVICES ═══ */}
        {allServices.length > 0 && (
          <FadeInSection delay={300}>
            <section className="max-w-5xl mx-auto px-5 sm:px-6 mt-20 sm:mt-28">
              <h2
                className="text-xl sm:text-2xl font-bold tracking-[-0.03em] mb-6"
                style={{ color: "var(--sf-text-primary)" }}
              >
                Services
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allServices.map((svc) => (
                  <Link
                    key={svc.id}
                    href={orgHref(`/book?cid=${coach.id}&sid=${svc.id}`)}
                    className="sf-card p-5 active:scale-[0.99]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3
                          className="text-md font-semibold tracking-[-0.01em]"
                          style={{ color: "var(--sf-text-primary)" }}
                        >
                          {svc.name}
                        </h3>
                        {svc.description && (
                          <p
                            className="text-tiny mt-1 line-clamp-2"
                            style={{ color: "var(--sf-text-secondary)" }}
                          >
                            {svc.description}
                          </p>
                        )}
                        <div
                          className="flex items-center gap-3 mt-2 text-xs font-medium"
                          style={{ color: "var(--sf-text-tertiary)" }}
                        >
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {svc.durationMinutes} min
                          </span>
                          {svc.maxParticipants > 1 && (
                            <span className="flex items-center gap-1">
                              <UserIcon className="h-3 w-3" />
                              Up to {svc.maxParticipants}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-md font-bold shrink-0" style={{ color: accent }}>
                        {formatPrice(getEffectivePrice(svc))}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </FadeInSection>
        )}

        {/* ═══ FOOTER ═══ */}
        <FadeInSection delay={450}>
          <footer
            className={`mt-20 sm:mt-28 px-5 sm:px-6 py-10 ${isDark ? "sf-footer-dark" : ""}`}
            style={
              !isDark
                ? { backgroundColor: "var(--sf-bg)", borderTop: "1px solid var(--sf-border)" }
                : {}
            }
          >
            <div className="max-w-5xl mx-auto relative">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  {org.logo && (
                    <Image
                      src={org.logo}
                      alt={org.name}
                      width={22}
                      height={22}
                      className="h-[22px] w-auto object-contain opacity-70"
                      unoptimized
                    />
                  )}
                  <span
                    className="text-tiny font-medium"
                    style={{ color: isDark ? "#A0A09B" : "var(--sf-text-secondary)" }}
                  >
                    {org.name}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end gap-3 sm:gap-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <Link
                      href={orgHref("/")}
                      className="text-tiny font-medium transition-colors hover:underline"
                      style={{ color: isDark ? "#6B6B66" : "var(--sf-text-tertiary)" }}
                    >
                      View all instructors
                    </Link>
                    <span
                      className="h-3 w-px"
                      style={{
                        backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "var(--sf-border)",
                      }}
                    />
                    <Link
                      href={orgHref("/privacy")}
                      className="text-tiny font-medium transition-colors hover:underline"
                      style={{ color: isDark ? "#6B6B66" : "var(--sf-text-tertiary)" }}
                    >
                      Privacy Policy
                    </Link>
                    <span
                      className="h-3 w-px"
                      style={{
                        backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "var(--sf-border)",
                      }}
                    />
                    <Link
                      href={orgHref("/terms")}
                      className="text-tiny font-medium transition-colors hover:underline"
                      style={{ color: isDark ? "#6B6B66" : "var(--sf-text-tertiary)" }}
                    >
                      Terms of Service
                    </Link>
                  </div>
                  <span
                    className="h-3 w-px hidden sm:block"
                    style={{
                      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "var(--sf-border)",
                    }}
                  />
                  <p
                    className="text-micro"
                    style={{ color: isDark ? "#4A4A47" : "var(--sf-text-muted)" }}
                  >
                    &copy; {new Date().getFullYear()} {org.name}
                  </p>
                </div>
              </div>
            </div>
          </footer>
        </FadeInSection>
      </div>

      {/* ═══ MOBILE FLOATING CTA ═══ */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden pb-[env(safe-area-inset-bottom)]">
        <div
          className="mx-4 mb-4 rounded-2xl px-5 py-4 backdrop-blur-xl flex items-center justify-between"
          style={{
            backgroundColor: "rgba(255,255,255,0.95)",
            border: "1px solid var(--sf-border)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <div>
            <p className="text-sm font-semibold" style={{ color: "var(--sf-text-primary)" }}>
              {coach.name}
            </p>
            <p className="text-xs" style={{ color: "var(--sf-text-secondary)" }}>
              {allServices.length} {allServices.length === 1 ? "service" : "services"} available
            </p>
          </div>
          <Link
            href={orgHref(`/book?cid=${coach.id}`)}
            className="text-sm font-semibold text-white px-5 py-2.5 rounded-xl transition-all hover:opacity-90 active:scale-[0.97]"
            style={{ backgroundColor: accent }}
          >
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
}
